'use client'

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { useController, Control, FieldPath, FieldValues } from 'react-hook-form'
import { useDirtyFields } from '../../context/dirty-fields-context'
import InputGroup from '@/shared/components/inputs/input-group'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { useSession } from '@/features/auth/context/session.context'
import { useProjectNotesTagged, type TaggedVolunteer } from '../../context/project-notes-tagged-context'
import { X } from 'lucide-react'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { cn } from '@/shared/lib/utils'

type ProjectGeneralNotesWithMentionsProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName
  control: Control<TFieldValues>
  defaultRows?: number
  labelAction?: React.ReactNode
  inputGroupProps?: { label?: string; wrapperClassName?: string }
  readOnly?: boolean
}

function formatDateStamp(fullname: string | null | undefined): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const year = String(now.getFullYear()).slice(-2)
  const userName = fullname || 'Unknown User'
  return `${month}/${day}/${year} ${userName}:`
}

export default function ProjectGeneralNotesWithMentions<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  defaultRows = 4,
  labelAction,
  inputGroupProps,
  readOnly = false,
}: ProjectGeneralNotesWithMentionsProps<TFieldValues, TName>) {
  const { profile } = useSession()
  const { taggedVolunteers, setTaggedVolunteers, setNoteSource } = useProjectNotesTagged()
  const sectionLabel = inputGroupProps?.label ?? 'Project General Notes'
  const { isFieldDirty } = useDirtyFields()
  const isDirty = isFieldDirty(name as string)

  const { field, fieldState } = useController({ control, name })
  const notes = field.value ?? ''

  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionStart, setMentionStart] = useState(0)
  const [mentionEnd, setMentionEnd] = useState(0)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [mentionDropdownRect, setMentionDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [isNotesExpanded, setIsNotesExpanded] = useState(false)
  const pendingSelectionRef = useRef<number | null>(null)
  const mentionListRef = useRef<HTMLUListElement | null>(null)

  const { data: teamLeadersData } = useQuery({
    queryKey: ['volunteers-team-leaders'],
    queryFn: async () => {
      const res = await fetch('/api/volunteers-team-leaders')
      if (!res.ok) throw new Error('Failed to fetch')
      const json = await res.json()
      return json.success && json.data ? json.data : []
    },
  })

  const volunteerOptions = useMemo(() => {
    if (!Array.isArray(teamLeadersData)) return []
    const taggedIds = new Set(taggedVolunteers.map((v) => v.id))
    return teamLeadersData
      .filter((u: { id?: number; fullname?: string }) => u.id != null && !taggedIds.has(u.id))
      .map((u: { id: number; fullname?: string }) => ({ id: u.id, fullname: u.fullname || 'Unknown' }))
  }, [teamLeadersData, taggedVolunteers])

  const filteredMentionOptions = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase()
    if (!q) return volunteerOptions.slice(0, 10)
    return volunteerOptions
      .filter((v) => v.fullname.toLowerCase().includes(q))
      .slice(0, 10)
  }, [volunteerOptions, mentionQuery])

  const addTagged = useCallback(
    (v: TaggedVolunteer) => {
      setNoteSource(sectionLabel)
      setTaggedVolunteers((prev) => [...prev, v])
      setMentionQuery('')
      setMentionOpen(false)
    },
    [setTaggedVolunteers, setNoteSource, sectionLabel]
  )

  const insertMentionAndAddTagged = useCallback(
    (v: TaggedVolunteer) => {
      const insert = `@${v.fullname} `
      const newNotes = notes.slice(0, mentionStart) + insert + notes.slice(mentionEnd)
      field.onChange(newNotes)
      addTagged(v)
      pendingSelectionRef.current = mentionStart + insert.length
    },
    [notes, mentionStart, mentionEnd, field, addTagged]
  )

  const removeTagged = useCallback(
    (id: number) => {
      setTaggedVolunteers((prev) => prev.filter((t) => t.id !== id))
    },
    [setTaggedVolunteers]
  )

  useEffect(() => {
    const position = pendingSelectionRef.current
    if (position === null) return
    pendingSelectionRef.current = null
    const raf = requestAnimationFrame(() => {
      const el = textareaRef.current
      const value = field.value ?? ''
      if (el && position >= 0 && position <= value.length) {
        el.focus()
        el.setSelectionRange(position, position)
        el.scrollTop = el.scrollHeight - el.clientHeight
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [field.value])

  const updateMentionRect = useCallback(() => {
    if (!textareaRef.current) return
    const rect = textareaRef.current.getBoundingClientRect()
    setMentionDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width })
  }, [])

  useEffect(() => {
    if (!mentionOpen || !textareaRef.current) {
      setMentionDropdownRect(null)
      return
    }
    updateMentionRect()
    window.addEventListener('scroll', updateMentionRect, true)
    window.addEventListener('resize', updateMentionRect)
    return () => {
      window.removeEventListener('scroll', updateMentionRect, true)
      window.removeEventListener('resize', updateMentionRect)
    }
  }, [mentionOpen, notes, updateMentionRect])

  useEffect(() => {
    if (!mentionOpen || !mentionListRef.current) return
    const selected = mentionListRef.current.children[mentionSelectedIndex] as HTMLElement | undefined
    selected?.scrollIntoView({ block: 'nearest' })
  }, [mentionOpen, mentionSelectedIndex])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (readOnly) return
      const value = e.target.value
      const start = e.target.selectionStart ?? value.length
      field.onChange(value)
      const beforeCursor = value.slice(0, start)
      const lastAt = beforeCursor.lastIndexOf('@')
      const hasSpaceAfterAt = lastAt >= 0 && beforeCursor.slice(lastAt + 1).includes(' ')
      if (lastAt >= 0 && !hasSpaceAfterAt) {
        setMentionStart(lastAt)
        setMentionEnd(start)
        setMentionQuery(beforeCursor.slice(lastAt + 1))
        setMentionOpen(true)
        setMentionSelectedIndex(0)
      } else {
        setMentionOpen(false)
      }
    },
    [field, readOnly]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (readOnly) return
      if (mentionOpen && filteredMentionOptions.length > 0) {
        if (e.key === 'Escape') {
          e.preventDefault()
          setMentionOpen(false)
          return
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          const v = filteredMentionOptions[mentionSelectedIndex]
          if (v) insertMentionAndAddTagged(v)
          return
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setMentionSelectedIndex((i) => (i < filteredMentionOptions.length - 1 ? i + 1 : 0))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setMentionSelectedIndex((i) => (i > 0 ? i - 1 : filteredMentionOptions.length - 1))
          return
        }
      }
      if (e.key !== 'Enter') return
      const el = textareaRef.current
      const start = el?.selectionStart ?? notes.length
      const end = el?.selectionEnd ?? start
      const hasContent = notes.trim().length > 0
      if (!el || !hasContent) return
      const dateStamp = formatDateStamp(profile?.fullname)
      const before = notes.slice(0, start)
      const after = notes.slice(end)
      const lastNewline = before.lastIndexOf('\n')
      const currentLine = lastNewline === -1 ? before : before.slice(lastNewline + 1)
      const isEmptyStampedLine = currentLine === dateStamp || currentLine === dateStamp + ' '
      if (isEmptyStampedLine) {
        requestAnimationFrame(() => {
          const ta = textareaRef.current
          if (ta) ta.scrollTop = ta.scrollHeight - ta.clientHeight
        })
        return
      }
      e.preventDefault()
      const newValue = `${before}\n${dateStamp} ${after}`
      const newCursorPos = before.length + 1 + dateStamp.length + 1
      pendingSelectionRef.current = newCursorPos
      field.onChange(newValue)
    },
    [
      notes,
      profile?.fullname,
      field,
      mentionOpen,
      filteredMentionOptions,
      mentionSelectedIndex,
      insertMentionAndAddTagged,
      readOnly,
    ]
  )

  const handleFocus = useCallback(() => {
    if (readOnly) return
    setNoteSource(sectionLabel)
    const current = (notes || '').trim()
    if (current === '') {
      const stamp = formatDateStamp(profile?.fullname) + ' '
      field.onChange(stamp)
      pendingSelectionRef.current = stamp.length
    }
  }, [notes, profile?.fullname, field, setNoteSource, sectionLabel, readOnly])

  return (
    <div className="space-y-4 w-full min-w-0">
      <div className="flex flex-nowrap items-center gap-2 w-full min-w-0">
        <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
          <span className="text-sm font-medium shrink-0">{inputGroupProps?.label ?? 'Project General Notes'}</span>
          {labelAction}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsNotesExpanded(!isNotesExpanded)}
          className="h-6 w-6 p-0 shrink-0 ml-auto text-muted-foreground hover:text-foreground"
          aria-label={isNotesExpanded ? 'Collapse notes' : 'Expand notes to read more'}
        >
          <ChevronDownIcon
            className={cn('h-4 w-4 transition-transform duration-200', isNotesExpanded && 'rotate-180')}
          />
        </Button>
      </div>
      {taggedVolunteers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-muted-foreground">Notify on save:</span>
          {taggedVolunteers.map((v) => (
            <span
              key={v.id}
              className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary px-2 py-0.5 text-sm"
            >
              {v.fullname}
              <button
                type="button"
                aria-label={`Remove ${v.fullname} from tag list`}
                className="hover:bg-primary/20 rounded p-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => removeTagged(v.id)}
                disabled={readOnly}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <InputGroup
          wrapperClassName={inputGroupProps?.wrapperClassName}
          error={fieldState.error?.message}
          isDirty={isDirty}
        >
          <div
            className={cn(
              'overflow-y-auto rounded-md border border-input transition-[max-height] duration-200',
              isNotesExpanded ? 'max-h-[320px]' : 'max-h-[88px]'
            )}
          >
            <Textarea
              ref={textareaRef}
              name={field.name}
              value={notes}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={() => {
                setMentionOpen(false)
                field.onBlur()
              }}
              placeholder="Add dated notes... (type @ to tag a volunteer)"
              className={cn(
                "min-h-[80px] resize-y border-0 rounded-md focus-visible:ring-0 focus-visible:ring-offset-0",
                readOnly && "bg-muted/40 text-muted-foreground cursor-not-allowed"
              )}
              rows={defaultRows}
              readOnly={readOnly}
              disabled={readOnly}
            />
          </div>
        </InputGroup>
        {mentionOpen &&
          mentionDropdownRect &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              className="fixed z-[100] rounded-md border border-input bg-popover text-popover-foreground shadow-md overflow-hidden"
              role="listbox"
              style={{
                top: mentionDropdownRect.top,
                left: mentionDropdownRect.left,
                width: mentionDropdownRect.width,
                minWidth: 200,
              }}
            >
              <div className="p-2 max-h-52 overflow-y-auto">
                {filteredMentionOptions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2 px-2">
                    {volunteerOptions.length === 0
                      ? taggedVolunteers.length > 0
                        ? 'All volunteers are already tagged.'
                        : 'No volunteers available.'
                      : `No match for "${mentionQuery.trim() || '@'}"`}
                  </p>
                ) : (
                  <ul ref={mentionListRef} className="space-y-0.5">
                    {filteredMentionOptions.map((v, i) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={i === mentionSelectedIndex}
                          className={`w-full text-left text-sm py-2 px-3 rounded ${
                            i === mentionSelectedIndex ? 'bg-muted' : 'hover:bg-muted/70'
                          }`}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            insertMentionAndAddTagged(v)
                          }}
                        >
                          {v.fullname}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  )
}
